import { useCallback, useRef, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';

/**
 * The phone's bottom sheet, as Flobi Drive draws it (`flobi_drive` MobileSheet): a grabber, rounded
 * top corners, at most 85% of the screen's height, the home bar's room below, and swiped down to go
 * away. Artwork's drawers (Properties, Layers, Assets, Versions, Comments) and every popover and menu
 * stand in one of these below 768 px: tall for a library, mid where the canvas is still worked on (Layers,
 * Comments), and only as tall as its content for Properties (Artwork A, Mohamad's pick, 2026-10-26).
 *
 * Only the grabber starts the drag, so a list scrolling inside the sheet never fights it. There is no
 * scrim: the canvas above stays live, so a colour can be watched while it is changed.
 */

/** How far a sheet is pulled down, or how fast, before letting go closes it (Drive's numbers). */
export const CLOSE_DISTANCE = 120;
export const CLOSE_SPEED = 0.8;

/** Whether a pull that ended here closes the sheet: far enough, or flicked. */
export function pullCloses(distance: number, millis: number): boolean {
  if (distance > CLOSE_DISTANCE) return true;
  return distance > 24 && millis > 0 && distance / millis > CLOSE_SPEED;
}

/** The grabber's handlers: it drags the sheet down with the finger and closes it when let go far enough. */
export function useSheetPull(onClose: () => void) {
  const sheet = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const element = sheet.current;
    if (!element || event.button !== 0) return;
    const startY = event.clientY;
    const startAt = performance.now();
    const handle = event.currentTarget;
    handle.setPointerCapture?.(event.pointerId);
    element.style.transition = 'none';
    const move = (e: PointerEvent) => {
      const down = Math.max(0, e.clientY - startY);
      element.style.transform = down ? `translateY(${down}px)` : '';
    };
    const up = (e: PointerEvent) => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      handle.removeEventListener('pointercancel', up);
      const down = Math.max(0, e.clientY - startY);
      element.style.transition = '';
      if (e.type === 'pointerup' && pullCloses(down, performance.now() - startAt)) {
        closeRef.current();
        return;
      }
      element.style.transform = '';
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
    handle.addEventListener('pointercancel', up);
  }, []);
  return { sheet, grab: { onPointerDown } };
}

/** The grabber bar itself, the drag's handle. */
export function SheetGrabber({ onPointerDown }: { onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void }) {
  return (
    <div className="phone-sheet-grabber" onPointerDown={onPointerDown} aria-hidden>
      <span />
    </div>
  );
}

/**
 * A drawer as a bottom sheet. The panel inside keeps its own header and close button; the sheet only
 * places it, so the laptop's panel and the phone's are the same component.
 */
export function PhoneSheet({ label, onClose, children, size = 'tall', className = '', style }: { label: string; onClose: () => void; children: ReactNode; size?: 'tall' | 'mid' | 'half'; className?: string; style?: CSSProperties }) {
  const { sheet, grab } = useSheetPull(onClose);
  return (
    <div ref={sheet} role="dialog" aria-label={label} data-size={size} className={`phone-sheet ${className}`} style={style}>
      <SheetGrabber {...grab} />
      <div className="phone-sheet-content">{children}</div>
    </div>
  );
}
